import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { getServingAvailabilityRequest, setServingAvailability } from '../../utils/api';

// General servant-leader "preferred serving times" — a no-login page sent to an SL.
// The SL marks which day/time-of-day slots they can serve; their team lead uses
// it to build the schedule. Distinct from the Kids "most helpful 3" flow.

const ACCENT = '#202020';

const DEMO_DATA = {
  person: { id: 'demo', name: 'Jordan' },
  days: ['Thursday', 'Friday', 'Saturday', 'Sunday'],
  slots: [
    { key: 'AM', label: 'Morning' },
    { key: 'PM', label: 'Afternoon' },
    { key: 'Eve', label: 'Evening' },
  ],
  existing: { selected: [], note: '' },
};

export default function ServingAvailabilityPage() {
  const { personId } = useParams();
  const isDemo = personId === 'demo';
  const [data, setData] = useState(isDemo ? DEMO_DATA : null);
  const [loading, setLoading] = useState(!isDemo);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(() => new Set());
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  // Save failures show inline — the page-level `error` replaces the whole form
  // with "Link not found", which would throw away everything they filled in.
  const [saveError, setSaveError] = useState('');

  useEffect(() => {
    if (isDemo) return;
    let cancelled = false;
    getServingAvailabilityRequest(personId)
      .then(d => {
        if (cancelled) return;
        setData(d);
        setSelected(new Set(d?.existing?.selected || []));
        setNote(d?.existing?.note || '');
        setLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setError('We couldn’t load your availability request. The link may have expired — contact your team lead.');
        setLoading(false);
      });
    return () => { cancelled = true; };
  }, [isDemo, personId]);

  function cellKey(day, slotKey) { return `${day}|${slotKey}`; }

  function toggle(day, slotKey) {
    setSaved(false);
    setSelected(prev => {
      const next = new Set(prev);
      const k = cellKey(day, slotKey);
      if (next.has(k)) next.delete(k);
      else next.add(k);
      return next;
    });
  }

  async function handleSubmit() {
    setSaving(true);
    const payload = {
      personId: data.person.id,
      selected: Array.from(selected),
      note,
    };
    setSaveError('');
    try {
      if (!isDemo) await setServingAvailability(payload);
      setSaved(true);
    } catch {
      setSaveError('Something went wrong saving your response. Please try again.');
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: 'var(--off)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ fontFamily: 'var(--font-b)', fontSize: 15, color: 'var(--muted)' }}>Loading…</div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div style={{ minHeight: '100vh', background: 'var(--off)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ maxWidth: 420, textAlign: 'center', padding: '0 24px' }}>
          <div style={{ fontFamily: 'var(--font-h)', fontSize: 18, fontWeight: 900, color: 'var(--black)', marginBottom: 12 }}>Link not found</div>
          <div style={{ fontFamily: 'var(--font-b)', fontSize: 15, color: 'var(--muted)', lineHeight: 1.6 }}>
            {error || 'This link is invalid or has expired. Contact your team lead.'}
          </div>
        </div>
      </div>
    );
  }

  const days = data.days || [];
  const slots = data.slots || [];

  return (
    <div style={{ width: '100%', minHeight: '100vh', background: 'var(--off)' }}>
      <div style={{ maxWidth: 560, margin: '0 auto', padding: '40px 24px 80px' }}>

        {/* Header */}
        <div style={{ marginBottom: 24 }}>
          <a
            href={`/s/${personId}`}
            style={{ display: 'inline-block', fontFamily: 'var(--font-h)', fontSize: 12, fontWeight: 700, color: 'var(--mid)', textDecoration: 'none', marginBottom: 10 }}
          >
            ← Back to my schedule
          </a>
          <div style={{ fontFamily: 'var(--font-h)', fontSize: 12, fontWeight: 700, color: 'var(--muted)', marginBottom: 8 }}>
            The Belonging Co · Annual Conference 2026
          </div>
          <div style={{ fontFamily: 'var(--font-h)', fontSize: 28, fontWeight: 900, color: 'var(--black)', lineHeight: 1.1 }}>
            When Can You Serve?
          </div>
          <div style={{ fontFamily: 'var(--font-b)', fontSize: 14, fontWeight: 300, color: 'var(--muted)', marginTop: 4 }}>
            No login required — this page was sent to your email.
          </div>
        </div>

        {saved ? (
          <div className="avail-form-card" style={{ borderTop: `4px solid ${ACCENT}` }}>
            <div style={{ fontFamily: 'var(--font-h)', fontSize: 18, fontWeight: 900, color: 'var(--black)', marginBottom: 8 }}>
              Thanks, {data.person.name}! ✓
            </div>
            <div style={{ fontFamily: 'var(--font-b)', fontSize: 14, fontWeight: 300, color: 'var(--mid)', lineHeight: 1.6 }}>
              You marked <strong>{selected.size}</strong> time{selected.size === 1 ? '' : 's'} you can serve. Your team lead will build your schedule from here. You can come back to this link any time to update it.
            </div>
            <button className="btn btn-sm" style={{ marginTop: 16 }} onClick={() => setSaved(false)}>Edit my response</button>
          </div>
        ) : (
          <div className="avail-form-card" style={{ borderTop: `4px solid ${ACCENT}` }}>
            <div style={{ fontFamily: 'var(--font-b)', fontSize: 14, fontWeight: 300, color: 'var(--mid)', marginBottom: 20, lineHeight: 1.6 }}>
              Hi {data.person.name}! Tap every day and time you’re <strong style={{ color: 'var(--black)' }}>available to serve</strong>. Check as many as you can — your team lead will build the actual schedule from what you pick.
            </div>

            {days.length === 0 ? (
              <div style={{ padding: '16px', fontFamily: 'var(--font-b)', fontSize: 13, color: 'var(--muted)', border: '1px solid var(--border)', borderRadius: 'var(--radius)' }}>
                The conference schedule isn’t set up yet. Check back soon.
              </div>
            ) : (
              <div style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius)', overflow: 'hidden', marginBottom: 20 }}>
                {days.map((day, di) => (
                  <div key={day} style={{ borderBottom: di === days.length - 1 ? 'none' : '1px solid var(--border)' }}>
                    <div style={{ fontFamily: 'var(--font-h)', fontSize: 11, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--muted)', padding: '12px 16px 8px', background: 'var(--off)' }}>
                      {day}
                    </div>
                    <div style={{ display: 'flex', gap: 8, padding: '4px 16px 14px', flexWrap: 'wrap' }}>
                      {slots.map(slot => {
                        const on = selected.has(cellKey(day, slot.key));
                        return (
                          <button
                            key={slot.key}
                            onClick={() => toggle(day, slot.key)}
                            style={{
                              flex: '1 1 0', minWidth: 96,
                              fontFamily: 'var(--font-h)', fontWeight: 800, fontSize: 13, padding: '12px 10px',
                              borderRadius: 'var(--radius)', cursor: 'pointer',
                              border: `2px solid ${on ? ACCENT : 'var(--border)'}`,
                              background: on ? ACCENT : 'var(--white)',
                              color: on ? '#fff' : 'var(--mid)',
                              transition: 'all 0.12s',
                            }}
                          >
                            {slot.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Optional note */}
            <div className="form-group" style={{ marginBottom: 20 }}>
              <label className="form-label">Anything your team lead should know? (optional)</label>
              <textarea
                className="form-input"
                rows={3}
                placeholder="e.g. I can only serve Saturday morning, or I have a child in Kids…"
                value={note}
                onChange={e => { setNote(e.target.value); setSaved(false); }}
                style={{ resize: 'vertical', fontFamily: 'var(--font-b)', fontWeight: 300 }}
              />
            </div>

            <button
              onClick={handleSubmit}
              disabled={saving || selected.size === 0}
              style={{
                width: '100%', padding: '14px', fontFamily: 'var(--font-h)', fontWeight: 800, fontSize: 15,
                border: 'none', borderRadius: 'var(--radius)', cursor: (saving || selected.size === 0) ? 'not-allowed' : 'pointer',
                background: selected.size === 0 ? 'var(--border)' : ACCENT,
                color: selected.size === 0 ? 'var(--muted)' : '#fff',
              }}
            >
              {saving ? 'Saving…' : 'Submit my availability'}
            </button>
            {saveError && (
              <div style={{ marginTop: 12, background: '#fff0f0', border: '1px solid #c00', borderRadius: 'var(--radius)', padding: '11px 14px', fontFamily: 'var(--font-b)', fontSize: 13, color: '#900', textAlign: 'center' }}>
                {saveError}
              </div>
            )}
            {selected.size === 0 && days.length > 0 && (
              <div style={{ fontFamily: 'var(--font-b)', fontSize: 12, color: 'var(--muted)', textAlign: 'center', marginTop: 10 }}>
                Pick at least one time you can serve to submit.
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
